import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocale } from "@/context/LocaleContext";
import { supabase, SUPABASE_ENABLED } from "@/lib/supabase";

export default function Success() {
  const { t } = useLocale();

  const canonicalUrl =
    typeof window !== "undefined"
      ? window.location.origin + "/success"
      : undefined;

  const [lastOrder, setLastOrder] = useState<
    { id: string; quantity: number }[] | null
  >(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [downloadAvailable, setDownloadAvailable] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isBlogAccess, setIsBlogAccess] = useState(false);

  useEffect(() => {
    async function init() {
      // If Stripe redirected with a session_id, verify payment then request a download token from server
      try {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        if (sessionId) {
          try {
            // First, call verify-payment to ensure webhooks didn't miss the update
            try {
              const vres = await fetch(
                `/api/verify-payment?session_id=${encodeURIComponent(sessionId)}`,
              );
              const vbody = await vres.json();
              if (vres.ok && vbody && vbody.payment_status === "paid") {
                // Check if this is a blog access payment
                if (
                  SUPABASE_ENABLED &&
                  vbody?.notes === "Blog Premium Access - €199"
                ) {
                  // Save email to localStorage as reference (cache only, not source of truth)
                  const customerEmail = vbody?.customer_email;
                  if (customerEmail) {
                    localStorage.setItem(
                      "buildstore.blog.access.email",
                      customerEmail,
                    );
                  }
                  setIsBlogAccess(true);

                  // Update order as paid in Supabase (SOURCE OF TRUTH)
                  try {
                    if (vbody?.orderId) {
                      await supabase
                        .from("orders")
                        .update({ is_paid: true })
                        .eq("id", vbody.orderId)
                        .eq("tenant_id", 1);
                    }
                  } catch (err) {
                    console.warn("Error updating order paid status:", err);
                  }
                  return;
                }
                // proceed to mint download token for other products
              } else {
                // Not paid yet — do not attempt download token
                console.warn("Payment not verified as paid yet:", vbody);
                // continue to fallback behavior (local storage)
                throw new Error("Payment not completed");
              }
            } catch (err) {
              console.warn(
                "verify-payment check failed or session not paid:",
                err,
              );
              // If verify failed or not paid, avoid calling download-token which will return 403.
              throw err;
            }

            // If we reached here, payment is verified as paid — request download token
            try {
              const res = await fetch(
                `/api/download-token?session_id=${encodeURIComponent(sessionId)}`,
              );
              const body = await res.json();
              if (res.ok && body?.token && body?.product) {
                // open download in new tab
                const url = `/api/download?product=${encodeURIComponent(body.product)}&token=${encodeURIComponent(body.token)}`;
                window.open(url, "_blank");
                setDownloadAvailable(true);
                setSelectedProduct(body.product);
                return;
              } else {
                console.warn("/api/download-token returned error", body);
              }
            } catch (err) {
              console.warn("Error fetching download token", err);
            }
          } catch (err) {
            // verify failed or not paid: fallthrough to local fallback logic below
          }
        }
      } catch (err) {
        console.warn("Success init error", err);
      }

      // fallback: read lastOrder from localStorage (older flow)
      try {
        const raw = localStorage.getItem("fusion.lastOrder");
        if (raw) {
          const items = JSON.parse(raw) as { id: string; quantity: number }[];
          setLastOrder(items);
          // allow download for any sport-gear-store package (basic, pro, premium)
          const sportItem = items.find(
            (it) => it.id && String(it.id).startsWith("sport-gear-store"),
          );
          if (sportItem) {
            setSelectedProduct(sportItem.id);
            setDownloadAvailable(true);
          }
        }
      } catch {}

      // clear local storage value after reading
      try {
        localStorage.removeItem("fusion.lastOrder");
      } catch {}
    }
    init();
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloadError(null);
    if (!selectedProduct)
      return setDownloadError(
        t("success.no_product_selected") || "No product selected",
      );

    setIsDownloading(true);
    try {
      // Open the download endpoint in a new tab to avoid proxy/XHR streaming issues (502).
      const url = `/api/download?product=${encodeURIComponent(selectedProduct)}`;
      window.open(url, "_blank");
      return;
    } catch (err: any) {
      console.error("Download error", err);
      setDownloadError(err?.message || t("error.download_failed"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Payment Successful - BuildStore</title>
        <meta
          name="description"
          content="Your payment has been processed successfully. Download your package and start building your e-commerce store."
        />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:title" content="Payment Successful - BuildStore" />
        <meta
          property="og:description"
          content="Your payment has been processed successfully. Download your package and start building your e-commerce store."
        />
        <meta
          property="og:image"
          content="https://www.buildstore.it/logo-buildstore-v4.png"
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <main className="container mx-auto pt-56 pb-20 text-center">
        <h1 className="text-3xl font-bold text-primary drop-shadow-[0_0_12px_hsl(var(--primary))]">
          {t("success.title")}
        </h1>

        {isBlogAccess ? (
          <div className="mt-6">
            <p className="text-white/80 mb-4">{t("blog.access.success")}</p>
            <Link
              to="/blog"
              className="inline-block rounded-lg bg-primary text-primary-foreground px-6 py-2 shadow-[0_0_20px_hsl(var(--primary))] hover:opacity-90"
            >
              {t("blog.access.success_cta")}
            </Link>
            <Link
              to="/"
              className="ml-3 inline-block rounded-lg border border-white/10 px-4 py-2 text-white/80"
            >
              Back to Home
            </Link>
          </div>
        ) : downloadAvailable ? (
          <div className="mt-6">
            {(() => {
              const titleMap: Record<string, string> = {
                "sport-gear-store-basic": t(
                  "product.sport-gear-store-basic.name",
                ),
                "sport-gear-store": t("product.sport-gear-store.name"),
                "sport-gear-store-premium": t(
                  "product.sport-gear-store-premium.name",
                ),
              };
              const title = selectedProduct
                ? titleMap[selectedProduct] || t("packages.title")
                : t("packages.title");

              let msgKey = "success.download_msg_other";
              if (selectedProduct === "sport-gear-store-basic")
                msgKey = "success.download_msg_basic";
              else if (selectedProduct === "sport-gear-store")
                msgKey = "success.download_msg_pro";
              else if (selectedProduct === "sport-gear-store-premium")
                msgKey = "success.download_msg_premium";

              return (
                <p className="text-white/80">{t(msgKey, { variant: title })}</p>
              );
            })()}

            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => {
                  if (!selectedProduct) return;
                  window.open(
                    `/api/download?product=${encodeURIComponent(selectedProduct)}`,
                    "_blank",
                  );
                }}
                disabled={isDownloading}
                aria-busy={isDownloading}
                className={`rounded-lg bg-primary text-primary-foreground px-6 py-2 hover:opacity-90 ${isDownloading ? "opacity-70 cursor-wait" : ""}`}
              >
                {isDownloading
                  ? t("success.downloading") || "Downloading..."
                  : t("success.download_now")}
              </button>
              <Link
                to="/"
                className="inline-block rounded-lg border border-white/10 px-4 py-2 text-white/80"
              >
                {t("success.back_to_shop")}
              </Link>

              {selectedProduct === "sport-gear-store-premium" && (
                <Link
                  to="/admin"
                  className="inline-block rounded-lg border border-white/10 px-4 py-2 text-white/80"
                >
                  {t("success.go_admin")}
                </Link>
              )}
            </div>
            {downloadError && (
              <div className="mt-3 text-red-300" role="alert">
                {downloadError}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6">
            <p className="text-white/80">{t("success.thanks")}</p>
            <Link
              to="/"
              className="mt-6 inline-block rounded-lg bg-primary text-primary-foreground px-6 py-2 shadow-[0_0_20px_hsl(var(--primary))] hover:opacity-90"
            >
              {t("success.back_to_shop")}
            </Link>
          </div>
        )}
      </main>
    </>
  );
}
